import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Download,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  Library,
  Loader2,
  Play,
  Search,
  Video,
  X,
} from 'lucide-react'
import { adminLibraryApi } from '@/api/adminLibrary'
import type { LibraryResource, LibraryResourceType } from '@/api/adminLibrary'

type ResourceFilter = 'all' | LibraryResourceType

const TYPE_ICONS = {
  video: Video,
  text: FileText,
  image: ImageIcon,
  file: FileIcon,
} satisfies Record<LibraryResourceType, typeof Video>

function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`
}

export default function LibraryPage() {
  const { t, i18n } = useTranslation('admin')
  const [filter, setFilter] = useState<ResourceFilter>('all')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<LibraryResource | null>(null)

  const { data: resources = [], isLoading, isError } = useQuery({
    queryKey: ['lc', 'admin', 'library'],
    queryFn: adminLibraryApi.list,
    staleTime: 5 * 60 * 1000,
  })

  const counts = useMemo(() => {
    const next: Record<ResourceFilter, number> = { all: resources.length, video: 0, text: 0, image: 0, file: 0 }
    resources.forEach(resource => { next[resource.mediaType] += 1 })
    return next
  }, [resources])

  const visibleResources = useMemo(() => {
    const keyword = search.trim().toLocaleLowerCase(i18n.language)
    return resources.filter(resource => {
      if (filter !== 'all' && resource.mediaType !== filter) return false
      if (!keyword) return true
      return `${resource.title} ${resource.description ?? ''} ${resource.originalFilename ?? ''}`
        .toLocaleLowerCase(i18n.language)
        .includes(keyword)
    })
  }, [filter, i18n.language, resources, search])

  const filters: ResourceFilter[] = ['all', 'video', 'text', 'image', 'file']

  return (
    <div className="space-y-4 py-4">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">{t('library.title')}</h1>
        <p className="text-sm text-gray-500">{t('library.subtitle')}</p>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="group" aria-label={t('library.filterLabel')}>
            {filters.map(value => (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                  filter === value
                    ? 'border-primary-200 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                }`}
              >
                {t(`library.types.${value}`)} <span className="ml-1 opacity-60">{counts[value]}</span>
              </button>
            ))}
          </div>
          <label className="relative block w-full lg:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t('library.searchPlaceholder')}
              className="h-10 w-full rounded-md border border-gray-300 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none"
            />
          </label>
        </div>
      </section>

      {isLoading ? (
        <div className="flex min-h-72 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary-500" /></div>
      ) : isError ? (
        <EmptyState title={t('library.loadError')} description={t('library.loadErrorDescription')} />
      ) : visibleResources.length === 0 ? (
        <EmptyState title={t('library.empty')} description={t('library.emptyDescription')} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visibleResources.map(resource => (
            <ResourceCard key={resource.id} resource={resource} onOpen={() => setSelected(resource)} />
          ))}
        </div>
      )}

      {selected && <ResourceViewer resource={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}

function getDownloadFilename(resource: LibraryResource): string {
  const sourceName = resource.originalFilename || (resource.resourceUrl
    ? new URL(resource.resourceUrl, window.location.href).pathname.split('/').pop()
    : '') || ''
  const extension = sourceName.match(/\.([a-z0-9]+)$/i)?.[0] ?? ''
  const title = resource.title.trim() || sourceName
  const filename = extension && !title.toLowerCase().endsWith(extension.toLowerCase())
    ? `${title}${extension}`
    : title
  return filename.replace(/[<>:"/\\|?*]|[^\u0020-\uffff]/g, '_')
}

function ResourceCard({ resource, onOpen }: { resource: LibraryResource; onOpen: () => void }) {
  const { t, i18n } = useTranslation('admin')
  const Icon = TYPE_ICONS[resource.mediaType]
  const isDownload = resource.mediaType === 'file'
  const [isDownloading, setIsDownloading] = useState(false)

  async function handleDownload() {
    if (!resource.resourceUrl || isDownloading) return
    setIsDownloading(true)
    try {
      // A same-origin blob URL lets the browser honor the filename for CDN files.
      const response = await fetch(resource.resourceUrl)
      if (!response.ok) throw new Error(`Download failed: ${response.status}`)
      const blob = await response.blob()
      const filename = getDownloadFilename(resource)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      try {
        link.click()
      } finally {
        link.remove()
        // Allow the browser to start consuming the blob before releasing it.
        window.setTimeout(() => URL.revokeObjectURL(url), 1000)
      }
    } catch {
      toast.error(t('library.downloadError'))
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <article className="group flex min-h-56 flex-col rounded-lg border border-gray-200 bg-white p-5 transition-colors hover:border-gray-300 hover:bg-gray-50/40">
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600"><Icon className="h-5 w-5" /></div>
        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">{t(`library.types.${resource.mediaType}`)}</span>
      </div>
      <div className="mt-5 flex-1">
        <h2 className="line-clamp-2 font-semibold leading-6 text-gray-900">{resource.title}</h2>
        <p className="mt-2 line-clamp-2 text-sm leading-5 text-gray-500">{resource.description || t('library.noDescription')}</p>
      </div>
      <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
        <div className="text-xs text-gray-400">
          <p>{new Date(resource.createdAt).toLocaleDateString(i18n.language)}</p>
          {resource.fileSize > 0 && <p className="mt-0.5">{formatFileSize(resource.fileSize)}</p>}
        </div>
        {isDownload && resource.resourceUrl ? (
          <button type="button" onClick={handleDownload} disabled={isDownloading} aria-busy={isDownloading} className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-wait disabled:opacity-50">
            <Download className="h-4 w-4" />{t('library.download')}
          </button>
        ) : (
          <button type="button" onClick={onOpen} className="inline-flex items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100">
            <Play className="h-4 w-4" />{t('library.open')}
          </button>
        )}
      </div>
    </article>
  )
}

function ResourceViewer({ resource, onClose }: { resource: LibraryResource; onClose: () => void }) {
  const { t } = useTranslation('admin')
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl" onClick={event => event.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="min-w-0"><h2 className="truncate font-semibold text-gray-900">{resource.title}</h2>{resource.description && <p className="mt-0.5 truncate text-sm text-gray-500">{resource.description}</p>}</div>
          <button type="button" onClick={onClose} aria-label={t('library.close')} className="ml-4 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </header>
        <div className="max-h-[calc(90vh-73px)] overflow-auto bg-gray-50 p-5 md:p-8">
          {resource.mediaType === 'video' && resource.resourceUrl && <video src={resource.resourceUrl} controls className="mx-auto max-h-[70vh] w-full rounded-xl bg-black" />}
          {resource.mediaType === 'image' && resource.resourceUrl && <img src={resource.resourceUrl} alt={resource.title} className="mx-auto max-h-[70vh] rounded-xl object-contain shadow-lg" />}
          {resource.mediaType === 'text' && <div className="mx-auto max-w-3xl whitespace-pre-wrap rounded-lg border border-gray-200 bg-white p-6 text-sm leading-7 text-gray-700 md:p-10">{resource.content || t('library.emptyText')}</div>}
          {resource.mediaType !== 'text' && !resource.resourceUrl && <EmptyState title={t('library.unavailable')} description={t('library.unavailableDescription')} />}
        </div>
      </div>
    </div>
  )
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 text-center"><Library className="mb-4 h-9 w-9 text-gray-300" /><h2 className="font-semibold text-gray-700">{title}</h2><p className="mt-1 text-sm text-gray-500">{description}</p></div>
}
